import { useMemo } from 'react';

import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { SimpleTooltip } from '@bambi/component-library/src/components/Tooltip/SimpleTooltip';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { DateControls } from 'src/common/date/DateControls';
import { SelectedDateDisplay } from 'src/common/date/SelectedDateDisplay';
import { convertLocalDaysToDaysOfWeek } from 'src/features/trip/routeTemplates/forms/daysOfWeekMaps';
import { RouteTemplateFormModal } from 'src/features/trip/routeTemplates/modals/RouteTemplateFormModal';
import { routeTemplateActions } from 'src/features/trip/routeTemplates/routeTemplates.slice';

import { useDispatchRepository } from '../../common/useDispatchRepository';
import { dispatcherSlice } from '../../dispatcher.slice';
import { AssignedTripSearch } from '../AssignedTripSearch';
import { DispatchCalendarFilters } from './DispatchCalendarFilters/DispatchCalendarFilters';
import { DispatchCalendarHeaderMenu } from './DispatchCalendarHeaderMenu/DispatchCalendarHeaderMenu';
import { DispatchCalendarTimeColumn } from './DispatchCalendarTimeColumn/DispatchCalendarTimeColumn';
import { EstimatedRevenueBadge } from './EstimatedRevenueBadge';
import TripStatusAggregation from './TripStatusAggregation';

export function DispatchCalendarHeader() {
  const dispatch = useAppDispatch();
  const selectedDate = useAppSelector((state) => state.dispatcher.selectedDate);

  const { isFetching: dispatchFetching, data: dispatchData } =
    useDispatchRepository();

  const isMenuActionInProgress = useAppSelector(
    (state) => state.dispatcher.isMenuActionInProgress
  );

  const totalRevenue = useMemo(
    () =>
      dispatchData?.assignments.reduce(
        (acc, assignment) => acc + (assignment.estimated_revenue ?? 0),
        0
      ) ?? 0,
    [dispatchData?.assignments]
  );

  const navigate = useNavigate();

  return (
    <>
      <div className="w-full pb-3 pt-3 pr-3">
        <div className="grid w-full items-center sm:grid-cols-12">
          <div className="flex items-center sm:col-span-3">
            <DispatchCalendarTimeColumn />
            <SelectedDateDisplay date={DateTime.fromISO(selectedDate)} />
            {(dispatchFetching || isMenuActionInProgress) && (
              <div className="ml-4">
                <LoadingIndicator />
              </div>
            )}
          </div>
          <div className="flex justify-center sm:col-span-2">
            <DateControls
              current={DateTime.fromISO(selectedDate)}
              onPrev={() => {
                dispatch(
                  dispatcherSlice.actions.setSelectedDateToPreviousDay()
                );

                const previousDayISO = DateTime.fromISO(selectedDate)
                  .minus({ day: 1 })
                  .toISODate();
                dispatch(
                  routeTemplateActions.setFilterDaysOfWeek(
                    convertLocalDaysToDaysOfWeek([
                      DateTime.fromISO(previousDayISO).toFormat('cccc'),
                    ])
                  )
                );
                navigate(`/dispatch/${previousDayISO}`);
              }}
              onNext={() => {
                dispatch(dispatcherSlice.actions.setSelectedDateToNextDay());
                const nextDayISO = DateTime.fromISO(selectedDate)
                  .plus({ day: 1 })
                  .toISODate();

                dispatch(
                  routeTemplateActions.setFilterDaysOfWeek(
                    convertLocalDaysToDaysOfWeek([
                      DateTime.fromISO(nextDayISO).toFormat('cccc'),
                    ])
                  )
                );

                navigate(`/dispatch/${nextDayISO}`);
              }}
              onToday={() => {
                dispatch(dispatcherSlice.actions.setSelectedDateToToday());
                const todayISO = DateTime.local().toISODate();
                dispatch(
                  routeTemplateActions.setFilterDaysOfWeek(
                    convertLocalDaysToDaysOfWeek([
                      DateTime.fromISO(todayISO).toFormat('cccc'),
                    ])
                  )
                );

                navigate(`/dispatch/${todayISO}`);
              }}
              onDatePicked={(date) => {
                dispatch(
                  dispatcherSlice.actions.setSelectedDate(date?.toISODate())
                );

                if (date) {
                  dispatch(
                    routeTemplateActions.setFilterDaysOfWeek(
                      convertLocalDaysToDaysOfWeek([
                        DateTime.fromISO(date.toISODate()).toFormat('cccc'),
                      ])
                    )
                  );
                }
                navigate(`/dispatch/${date?.toISODate()}`);
              }}
              prevTooltip="Previous Day"
              nextTooltip="Next Day"
            />
            <div className="mx-2"></div>
          </div>
          <div className="flex items-center justify-end gap-x-1 sm:col-span-7">
            <div className="mr-2 max-w-[350px] flex-grow">
              <AssignedTripSearch />
            </div>
            <DispatchCalendarFilters />
            {dispatchData?.show_pricing ? (
              <div className="mr-2 flex items-center">
                <SimpleTooltip
                  trigger={
                    <div>
                      <EstimatedRevenueBadge revenue={totalRevenue} />
                    </div>
                  }
                  content="Total estimated revenue for the day's assignments"
                />
              </div>
            ) : null}
            <TripStatusAggregation />
            <DispatchCalendarHeaderMenu />
          </div>
        </div>
      </div>
      <RouteTemplateFormModal />
    </>
  );
}
